import {
    TableModalWithLayoutSettings,
    type TableModalWithLayoutSettingsProps,
} from '@Components/Tables/TableModalWithLayoutSettings';
import { useImportExternalErpItems } from '@Hooks/Stammdaten/useImportExternalErpItems';
import type { ExternalErpItem } from '@Hooks/Stammdaten/withExternalIdsAsIds';
import type { ApiImportExternalErpItemsRequest, EnumFrontendLayoutSetting } from '@Model';
import {
    type ListInfo,
    type ListRequest,
    type Result,
    Modal,
    Progress,
    useToasts,
} from '@synqo/frontend-component-library';
import { useCallback, useMemo } from 'react';

export interface ImportFlowControlProps {
    isOpen: boolean;
    onClose: () => void;
    onImported: () => void;
}

interface ImportExternalErpItemsFlowProps<TItem extends ExternalErpItem> extends ImportFlowControlProps {
    title: string;
    name: string;
    itemsLabel: string;
    columns: TableModalWithLayoutSettingsProps<TItem>['tableProps']['columns'];
    layoutSettingsContext: EnumFrontendLayoutSetting;
    loadImportable: (listRequest: ListRequest) => Promise<Result<ListInfo<TItem>>>;
    importSelected: (request: ApiImportExternalErpItemsRequest) => Promise<Result<unknown[]>>;
}

// @ai-generated 2026-10-22T13:20:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function ImportExternalErpItemsFlow<TItem extends ExternalErpItem>({
    isOpen,
    onClose,
    onImported,
    title,
    name,
    itemsLabel,
    columns,
    layoutSettingsContext,
    loadImportable,
    importSelected,
}: ImportExternalErpItemsFlowProps<TItem>) {
    const { pushToast, pushAllToasts } = useToasts();
    const { loadItems, importAsync, isImporting } = useImportExternalErpItems(loadImportable, importSelected);

    const handleImport = useCallback(
        async (selectedIds: string[]) => {
            if (selectedIds.length === 0) return;

            const result = await importAsync({ external_ids: selectedIds });
            pushAllToasts(result.messages);
            if (!result.data) return;

            pushToast({
                variant: 'success',
                title: 'Import abgeschlossen',
                message: `${result.data.length} von ${selectedIds.length} ${itemsLabel} importiert`,
            });
            onImported();
            onClose();
        },
        [importAsync, pushAllToasts, pushToast, itemsLabel, onImported, onClose],
    );

    const actions = useMemo(
        () => [{ name: 'import', label: isImporting ? 'Wird importiert...' : 'Importieren', action: handleImport }],
        [handleImport, isImporting],
    );

    const tableProps = useMemo(
        () => ({ name, columns, loader: loadItems, toolbarElements: [] }),
        [name, columns, loadItems],
    );

    if (isImporting) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} title={title} bodyProps={{ type: 'never' }} withoutCloseButton>
                <Progress id={`${name}-import-progress-bar`} animated size="md" ariaLabel="Import is running..." />
            </Modal>
        );
    }

    return (
        <TableModalWithLayoutSettings<TItem>
            title={title}
            isOpen={isOpen}
            onClose={onClose}
            layoutSettingsContext={layoutSettingsContext}
            multiple
            tableProps={tableProps}
            actions={actions}
        />
    );
}
